/**
 * CondensedListView — view "lista" do popover condensado
 * (Mensageria Condensada · superfície 2 · PR F1a).
 *
 * Composição: PinnedRail + CTA "Nova mensagem" (capability-aware) + lista compacta
 * de threads. A lista REUSA os helpers de exibição de `/mensagens` (thread-display)
 * para a densidade compacta — não forca a camada de dados (consome `useThreads`).
 *
 * CTA "Nova mensagem" (P19): só aparece para quem pode iniciar — supervisor
 * (`direct-message-initiate`, texto descendente/broadcast) ou atendente
 * (`direct-message-initiate-upward`, texto ascendente). Sem cap de iniciação, sem
 * CTA. Clicar muda para a view `compose` (preenchida em F1b).
 *
 * Chip âncora → `onPeek(conversationId)` (a view peek é F1b; aqui só roteia).
 */

import { Link2, Plus, Search, Users } from 'lucide-react';
import { useMemo, useState } from 'react';

import { useCapability } from '../../../../hooks/use-capability';
import { avatarColorFor, initialsOf } from '../../../../lib/format';
import { useAuthStore } from '../../../../stores/auth-store';
import { relativeTime } from '../../../conversations/format';
import { useThreads } from '../../hooks/use-direct-messages';
import { directCounterpartId, listThreadTitle, unreadDisplay } from '../../thread-display';

import { PinnedRail } from './PinnedRail';

import type { ThreadSummary } from '../../types';

interface CondensedListViewProps {
  onOpenThread: (threadId: string) => void;
  onCompose: () => void;
  onPeek: (conversationId: string) => void;
}

export function CondensedListView({
  onOpenThread,
  onCompose,
  onPeek,
}: CondensedListViewProps): JSX.Element {
  const { data: threads } = useThreads();
  const currentUserId = useAuthStore((s) => s.user?.id ?? null);

  const canInitiateDown = useCapability('direct-message-initiate');
  const canInitiateUp = useCapability('direct-message-initiate-upward');
  // Mensageria lateral/peer (opt-in) também habilita o CTA "Nova mensagem".
  const canInitiateLateral = useCapability('direct-message-initiate-lateral');
  const canInitiateAnyone = useCapability('direct-message-initiate-anyone');
  const canInitiate =
    canInitiateDown || canInitiateUp || canInitiateLateral || canInitiateAnyone;
  const newMsgHint = canInitiateDown
    ? 'para um colega, um time, ou broadcast'
    : canInitiateAnyone
      ? 'para qualquer pessoa da operação'
      : canInitiateLateral
        ? 'para colegas dos seus times ou seu supervisor'
        : 'para o seu supervisor ou líder de time';

  // Busca de conversas (P19): filtro client-side por nome da contraparte, mesmo
  // texto que a lista exibe. Vazio = lista completa. Sem backend.
  const [query, setQuery] = useState('');
  const needle = query.trim().toLowerCase();
  const filteredThreads = useMemo(
    () =>
      needle.length === 0
        ? threads
        : threads.filter((t) =>
            listThreadTitle(t, currentUserId).toLowerCase().includes(needle),
          ),
    [threads, needle, currentUserId],
  );

  const hasThreads = threads.length > 0;

  return (
    <div className="mc-view mc-view--flex">
      <PinnedRail onOpenThread={onOpenThread} />

      {canInitiate && (
        <div className="mc-section mc-section--divider">
          <button type="button" className="mc-newmsg-btn" onClick={onCompose}>
            <Plus size={16} strokeWidth={1.7} aria-hidden />
            <span className="mc-newmsg-label">
              <b>Nova mensagem</b>
              <span>{newMsgHint}</span>
            </span>
          </button>
        </div>
      )}

      {hasThreads && (
        <div className="mc-section mc-section--divider">
          <div className="mc-list-search">
            <Search size={14} strokeWidth={1.5} aria-hidden />
            <input
              type="text"
              className="mc-search-input"
              value={query}
              placeholder="Buscar conversas"
              aria-label="Buscar conversas"
              onChange={(e) => {
                setQuery(e.target.value);
              }}
            />
          </div>
        </div>
      )}

      {!hasThreads ? (
        <div className="mc-section">
          <span className="mc-mono-meta">Nenhuma mensagem ainda.</span>
        </div>
      ) : filteredThreads.length === 0 ? (
        <div className="mc-section">
          <span className="mc-mono-meta">Nenhuma conversa encontrada.</span>
        </div>
      ) : (
        <ul className="mc-thread-list mc-scroll" role="list">
          {filteredThreads.map((thread) => (
            <li key={thread.threadId}>
              <CondensedThreadItem
                thread={thread}
                currentUserId={currentUserId}
                onOpenThread={onOpenThread}
                onPeek={onPeek}
              />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

interface CondensedThreadItemProps {
  thread: ThreadSummary;
  currentUserId: string | null;
  onOpenThread: (threadId: string) => void;
  onPeek: (conversationId: string) => void;
}

/**
 * Item de thread em densidade compacta. Nome da contraparte vem resolvido do
 * backend (`counterpartName`, via `User.name`) — não depende de presença. Markup
 * `mc-*` do popover (não navega — abre inline).
 */
function CondensedThreadItem({
  thread,
  currentUserId,
  onOpenThread,
  onPeek,
}: CondensedThreadItemProps): JSX.Element {
  const isGroup = thread.kind === 'group';
  const hasUnread = thread.unread > 0;

  const counterpartId = directCounterpartId(thread, currentUserId);
  const title = listThreadTitle(thread, currentUserId);
  const avatarKey = counterpartId ?? thread.threadId;
  const initials = isGroup ? '' : initialsOf(thread.counterpartName ?? counterpartId ?? '');
  const preview = thread.lastMessagePreview ?? 'Sem mensagens ainda';
  const anchorId = thread.anchorConversationId;

  return (
    <button
      type="button"
      className={`mc-thread-item${hasUnread ? ' is-unread' : ''}`}
      onClick={() => {
        onOpenThread(thread.threadId);
      }}
    >
      {isGroup ? (
        <span className="mc-avatar mc-avatar--team" aria-hidden>
          <Users size={15} strokeWidth={1.6} />
        </span>
      ) : (
        <span className="mc-avatar" aria-hidden style={{ background: avatarColorFor(avatarKey) }}>
          {initials}
        </span>
      )}

      <span className="mc-thread-body">
        <span className="mc-thread-row1">
          <b className="mc-thread-name">{title}</b>
          <span className="mc-mono-meta">{relativeTime(thread.lastMessageAt)}</span>
        </span>
        <span className="mc-thread-preview">{preview}</span>
        {anchorId !== null && (
          // Chip âncora → porta para o peek (F1b). Genérico honesto (nome rico não
          // vem no contrato). stopPropagation: não abre o thread ao clicar no chip.
          <span
            className="mc-anchor-chip"
            role="button"
            tabIndex={0}
            aria-label="Sobre a conversa vinculada"
            onClick={(e) => {
              e.stopPropagation();
              onPeek(anchorId);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                e.stopPropagation();
                onPeek(anchorId);
              }
            }}
          >
            <Link2 size={10} strokeWidth={1.6} aria-hidden />
            Sobre a conversa
          </span>
        )}
      </span>

      {hasUnread && (
        <span className="mc-unread-pill" aria-label={`${thread.unread.toString()} não lidas`}>
          {unreadDisplay(thread.unread)}
        </span>
      )}
    </button>
  );
}
